import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import { Seo } from '../components/Seo';
import { 
  FileText
} from 'lucide-react';

const Terms = () => {

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Seo
        title="Terms of Service — CreateCollect"
        description="The terms governing use of CreateCollect, including plans and billing, payment processing fees, acceptable use, and your data."
        path="/terms"
      />
      {/* Header */}
      <PublicHeader secondaryLabel="About" secondaryHref="/about" />

      {/* Main Content */}
      <main className="flex-1 pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-16">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl font-heading font-bold mb-4" data-testid="terms-title">Terms of Service</h1>
            <p className="text-muted-foreground">
              Effective date: July 20, 2026
            </p>
          </div>

          {/* Section 1 */}
          <section className="mb-10" data-testid="terms-section-1">
            <h2 className="text-2xl font-heading font-semibold mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By creating an account or using CreateCollect, you agree to these Terms of Service. If you do not agree, do not use our service.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10" data-testid="terms-section-2">
            <h2 className="text-2xl font-heading font-semibold mb-4">2. Eligibility</h2>
            <p className="text-muted-foreground leading-relaxed">
              CreateCollect is intended for business use by adults 18 years of age or older. By using the service, you represent that you meet this requirement.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-10" data-testid="terms-section-3">
            <h2 className="text-2xl font-heading font-semibold mb-4">3. Your Account</h2>
            <p className="text-muted-foreground leading-relaxed">
              You are responsible for maintaining the security of your account credentials and for all activity that occurs under your account. If you suspect unauthorized access, notify us immediately at support@createcollect.com.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-10" data-testid="terms-section-4">
            <h2 className="text-2xl font-heading font-semibold mb-4">4. Acceptable Use</h2>
            <div className="text-muted-foreground leading-relaxed">
              <p className="mb-4">You may not use CreateCollect to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Send fraudulent invoices or misrepresent goods/services</li>
                <li>Conduct illegal activity or violate any applicable law</li>
                <li>Send unsolicited marketing emails to recipients who have not opted in</li>
                <li>Harass, abuse, or harm others</li>
                <li>Attempt to gain unauthorized access to our systems or other users&apos; accounts</li>
              </ul>
              <p className="mt-4">We reserve the right to suspend or terminate accounts that violate these terms.</p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-10" data-testid="terms-section-5">
            <h2 className="text-2xl font-heading font-semibold mb-4">5. Subscription Plans & Billing</h2>
            <p className="text-muted-foreground leading-relaxed">
              CreateCollect offers Free, Standard, and Pro plans. Paid plans are billed monthly via Stripe. You may cancel or change your plan at any time; changes take effect at the end of the current billing period. We reserve the right to change pricing with 30 days&apos; notice.
            </p>
            <p className="mb-4">
              <strong>Free trials.</strong> Some promotional codes offer a free trial that requires a payment method. Unless cancelled before the trial ends, the subscription automatically renews at the plan&apos;s regular monthly price, charged to the card on file. We email a reminder before the first charge, and you can cancel anytime from Settings.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-10" data-testid="terms-section-6">
            <h2 className="text-2xl font-heading font-semibold mb-4">6. Payment Processing Fees</h2>
            <p className="text-muted-foreground leading-relaxed">
              When your clients pay invoices online via Stripe, a processing fee of 3.4% + $0.30 per transaction is deducted from your payout. This covers Stripe&apos;s card processing costs plus platform costs. Your clients always pay the exact invoice amount.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-10" data-testid="terms-section-7">
            <h2 className="text-2xl font-heading font-semibold mb-4">7. Your Data</h2>
            <p className="text-muted-foreground leading-relaxed">
              You retain ownership of the invoices, client information, and other content you create. We do not claim any intellectual property rights over your data. See our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link> for details on how we handle your information.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-10" data-testid="terms-section-8">
            <h2 className="text-2xl font-heading font-semibold mb-4">8. Service Availability</h2>
            <p className="text-muted-foreground leading-relaxed">
              We strive for high uptime but do not guarantee uninterrupted service. Scheduled maintenance windows will be communicated in advance when possible.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-10" data-testid="terms-section-9">
            <h2 className="text-2xl font-heading font-semibold mb-4">9. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              CreateCollect is provided &quot;as is.&quot; To the maximum extent permitted by law, we disclaim all warranties, express or implied. We are not liable for any indirect, incidental, or consequential damages arising from your use of the service, including lost revenue or data.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-10" data-testid="terms-section-10">
            <h2 className="text-2xl font-heading font-semibold mb-4">10. Indemnification</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree to indemnify and hold harmless CreateCollect from any claims arising out of your use of the service, your content, or your violation of these terms.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-10" data-testid="terms-section-11">
            <h2 className="text-2xl font-heading font-semibold mb-4">11. Modifications</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update these Terms of Service from time to time. Material changes will be communicated via email or an in-app notice at least 14 days before taking effect. Continued use after changes constitutes acceptance.
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-10" data-testid="terms-section-12">
            <h2 className="text-2xl font-heading font-semibold mb-4">12. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              You may delete your account at any time through Settings. We may suspend or terminate accounts that violate these terms or for extended inactivity. Upon termination, your data will be deleted according to our data retention policy.
            </p>
          </section>

          {/* Section 13 */}
          <section className="mb-10" data-testid="terms-section-13">
            <h2 className="text-2xl font-heading font-semibold mb-4">13. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms are governed by the laws of the State of California, USA, without regard to conflict-of-law principles.
            </p>
          </section>

          {/* Section 14 */}
          <section className="mb-10" data-testid="terms-section-14">
            <h2 className="text-2xl font-heading font-semibold mb-4">14. Referral Program</h2>
            <p className="text-muted-foreground leading-relaxed">
              Referral rewards (free months or account credits) are granted when a referred user verifies their email and, for referrer rewards, sends their first invoice. Limit 12 referral credits per user per calendar year. Self-referrals, fake accounts, or automated signups void rewards. Credits have no cash value and expire if your account is deleted. We may modify or end the referral program at any time.
            </p>
          </section>

          {/* Section 15 */}
          <section className="mb-10" data-testid="terms-section-15">
            <h2 className="text-2xl font-heading font-semibold mb-4">15. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              Questions about these Terms? Reach us at{' '}
              <a href="mailto:support@createcollect.com" className="text-primary hover:underline">
                support@createcollect.com
              </a>.
            </p>
          </section>

          {/* Cross-link to Privacy */}
          <section className="mt-12 pt-8 border-t">
            <p className="text-muted-foreground">
              See also our{' '}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>{' '}
              for information on how we collect and handle your data.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Terms;
